import { LitElement, css, html } from "lit";
import { defineChromeElement } from "../../chrome/ChromeElement";
import { chromeControlStyles } from "../../chrome/chromeStyles";
import { fitTexts } from "../../chrome/fitText";

export const MONEY_LABEL_TAG = "slot-money-label";

/**
 * `<slot-money-label>`: a title over an amount — the balance, bet and win readouts. Desktop: the
 * orange Light title and the white amount left-aligned in their boxes; mobile: white, centred.
 * The boxes are custom properties (design units, y-down from the label's origin) the bar sets per
 * shape and label; the defaults are the portrait row's. `accent` paints the amount orange (the
 * bet while a feature mode is active).
 */
export class MoneyLabelElement extends LitElement {
  public static override properties = {
    title: { type: String, attribute: "heading" },
    amount: { type: String },
    layout: { type: String, reflect: true },
    accent: { type: Boolean, reflect: true },
    shown: { type: Boolean, reflect: true },
    amountShown: { type: Boolean, reflect: true, attribute: "amount-shown" },
  };

  public declare title: string;
  public declare amount: string;
  /** "desktop" (left-aligned) or "mobile" (centred). */
  public declare layout: "desktop" | "mobile";
  public declare accent: boolean;
  public declare shown: boolean;
  public declare amountShown: boolean;

  public constructor() {
    super();
    this.title = "";
    this.amount = "";
    this.layout = "mobile";
    this.accent = false;
    this.shown = true;
    this.amountShown = true;
  }

  public static override styles = [
    chromeControlStyles,
    css`
      :host {
        display: block; position: relative; color: #fff;
        --title-width: 403.1px; --title-height: 63.02px; --title-x: 0px; --title-y: -46.8px; --title-size: 46.2px;
        --amount-width: 403.1px; --amount-height: 77.27px; --amount-x: 0px; --amount-y: 12.83px;
      }
      :host(:not([shown])) { display: none; }
      .row { position: absolute; display: flex; align-items: center; overflow: visible; }
      .title { left: var(--title-x); top: var(--title-y); width: var(--title-width); height: var(--title-height); font-size: var(--title-size); font-weight: 300; }
      .amount { left: var(--amount-x); top: var(--amount-y); width: var(--amount-width); height: var(--amount-height); font-size: 55px; font-weight: 500; }
      :host([layout="mobile"]) .row { justify-content: center; transform: translate(-50%, -50%); }
      :host([layout="desktop"]) .row { justify-content: flex-start; transform: translateY(-50%); }
      :host([layout="desktop"]) .title { left: calc(var(--title-x) - var(--title-width) / 2); color: #fc8404; }
      :host([layout="desktop"]) .amount { left: calc(var(--amount-x) - var(--amount-width) / 2); }
      :host([layout="desktop"]) [data-fit] { transform-origin: left center; }
      :host([accent]) .amount { color: #fc8404; }
      :host(:not([amount-shown])) .amount { visibility: hidden; }
    `,
  ];

  protected override updated(): void {
    fitTexts(this.renderRoot);
  }

  protected override render() {
    return html`
      <div class="row title shadow"><span data-fit>${this.title}</span></div>
      <div class="row amount shadow" data-part="amount"><span data-fit>${this.amount}</span></div>
    `;
  }
}

export function defineMoneyLabelElement(): void {
  defineChromeElement(MONEY_LABEL_TAG, MoneyLabelElement);
}
